<template>
  <v-alert
    v-show="showAlert"
    :class="alertBaseClasses"
    :type="type"
    :outlined="outlined"
    v-bind="$attrs"
  >
    <v-btn
      v-if="collapsibleAlert"
      :ripple="false"
      :class="$style['alert-toggle']"
      @click="isVisible = !isVisible"
    >
      {{ !isVisible ? $t('general.moreInfo') : $t('general.ok') }}
      <IconSelect v-if="!isVisible" :class="$style.alertIcon" icon="info" />
      <IconSelect v-if="isVisible" :class="$style.alertIcon" icon="okay-circle" />
    </v-btn>
    <div :class="{ [$style.grid]: !!$slots.default }">
      <div class="base-alert__wrapper" :class="$style.wrapper">
        <div :class="$style.row">
          <IconSelect
            :class="$style.icon"
            :icon="computedIcon"
            :svg-props="{ height: iconSize, width: iconSize }"
          />
        </div>

        <div :class="[messageFlexClass, $style.text, `justify-center`, `justify-${alignment}`]">
          <template v-if="hasMessage">{{ message }}</template>
          <slot name="message" />
          <div v-if="!!$slots.additionalMessageRow">
            <slot name="additionalMessageRow" />
          </div>
        </div>
      </div>
      <slot />
    </div>
  </v-alert>
</template>

<script>
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'

export default {
  name: 'BaseAlert',
  components: {
    IconSelect,
  },
  props: {
    type: {
      type: String,
      default: 'info',
    },
    icon: {
      type: String,
      default: '',
    },
    iconSize: {
      type: Number,
      default: 24,
    },
    message: {
      type: String,
      default: '',
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    alignment: {
      type: String,
      default: 'center',
    },
    collapsibleAlert: {
      type: Boolean,
      default: false,
    },
    initiallyOpen: {
      type: Boolean,
      default: false,
    },
    alwaysShow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { isVisible: !this.collapsibleAlert || (this.collapsibleAlert && this.initiallyOpen) }
  },
  computed: {
    showAlert() {
      return this.alwaysShow || !!this.hasMessage || !!this.hasMessagesSlot
    },
    hasMessagesSlot() {
      return !!this.$slots.message
    },
    hasMessage() {
      return this.message !== ''
    },
    computedIcon() {
      return this.icon ? this.icon : this.defaultIcon
    },
    messageFlexClass() {
      return this.$slots.additionalMessageRow || this.$slots.message
        ? this.$style.col
        : this.$style.row
    },
    defaultIcon() {
      switch (this.type) {
        case 'success':
          return 'checkmark'
        case 'error':
          return 'warning'
        case 'warning':
          return 'warning-triangle'
        default:
          return 'info'
      }
    },
    alertBaseClasses() {
      return [
        this.$style.alert,
        {
          [this.$style['collapsibleAlert-hidden']]: !!this.collapsibleAlert,
          // [this.$style.hidden]: !this.isVisible,
          [this.$style['collapsibleAlert-visible']]: !!this.isVisible,
        },
      ]
    },
  },
}
</script>

<style lang="scss" module>
.alert {
  padding: 0 !important;

  color: currentColor !important;

  border-radius: 0 !important;

  :global(.v-alert__wrapper) {
    justify-content: center;

    > :global(.v-icon) {
      display: none;
    }
  }

  &:global(.v-alert--outlined) {
    background-color: var(--white) !important;
  }

  &:global(.v-alert__content) {
    @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
      flex-direction: row;
    }
    display: flex;
    flex-direction: column;
  }

  .row {
    display: flex;
    // flex-direction: column;
  }

  .col {
    display: flex;
    flex-direction: column;
  }

  &:global(.warning--text) {
    .text {
      color: var(--black);
    }
  }

  :global(.v-icon) {
    display: none;
  }
}

.text {
  flex: 1;
  width: 100%;
}

.grid {
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
}

.wrapper {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    grid-template-columns: min-content 1fr;
    place-items: start;

    padding: 1.25rem;
  }
  display: grid;
  place-items: center;
  place-self: center;

  gap: 1.25rem;

  padding: 1rem;

  &:global(.left) {
    justify-content: left;
  }

  &:global(.center) {
    justify-content: center;
  }

  &:global(.right) {
    justify-content: right;
  }
}

.icon {
  @media screen and (max-width: map-get($grid-breakpoints, 'md')) {
    margin: 0.5rem;

    transform: scale(1.5);
  }
}

.collapsibleAlert-hidden {
  transition: 0.8s all ease-in-out;

  .wrapper {
    gap: 0;
    padding: 0;
  }

  .grid {
    gap: 0;
  }

  .icon {
    display: none;
  }

  .text {
    height: 0px;
    overflow: hidden;
  }
}

.collapsibleAlert-visible {
  transition: 0.8s all ease-in-out;

  .text {
    height: auto;
    overflow: visible;
  }

  .wrapper {
    // grid-column: 1/5;
    padding: 1rem;
  }
}

.alert-toggle {
  grid-column: 1/5;
  order: -1;
  width: 100%;
  margin: 0.5rem;
  margin: 0 auto;

  color: var(--v-warning-base) !important;
  font-size: 1rem !important;

  background: transparent !important;
  box-shadow: none;
}

.alertIcon {
  margin-left: 0.5rem;
}
</style>
